// Strip across the top of every page showing trial / subscription state.
//   • read only (trial ended, payment failed, cancelled) — everyone sees it
//   • free trial — admins see how long is left (dismissible until the last week)
//   • payment problem in its grace period / cancelling — admins
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Sparkles, X, Lock } from 'lucide-react';
import { useApp } from '../../context/AppContext';

const KEY = 'clockout-billing-banner-dismissed';

function readDismissed() {
  try { return sessionStorage.getItem(KEY) || ''; } catch { return ''; }
}

const fmtDate = (v) => {
  try { return new Date(v).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' }); } catch { return ''; }
};

export default function BillingBanner() {
  const { billing, currentUser } = useApp();
  const [dismissed, setDismissed] = useState(readDismissed);
  const role = currentUser?.role;
  const isAdmin = role === 'admin' || role === 'super_admin';
  const access = billing?.access;
  if (!access || (role === 'super_admin' && access.state === 'read_only')) return null;

  let tone = 'warn';
  let icon = Sparkles;
  let text = null;
  let cta = null;
  let dismissKey = null;

  if (access.state === 'read_only') {
    tone = 'bad';
    icon = Lock;
    text = billing.message || 'Your account is read only at the moment.';
    if (isAdmin) cta = access.reason === 'payment_failed' ? 'Update payment' : 'Choose a plan';
  } else if (access.reason === 'trial' && isAdmin) {
    const days = access.trialDaysLeft ?? 0;
    text = days <= 1
      ? 'Your free trial ends tomorrow.'
      : `Your free trial has ${days} days left${billing.trialEndsAt ? ` (ends ${fmtDate(billing.trialEndsAt)})` : ''}.`;
    cta = 'Choose a plan';
    // Dismissible until the last week
    if (days > 7) dismissKey = `trial-${days}`;
  } else if (access.warning === 'payment_failed' && isAdmin) {
    tone = 'bad';
    icon = AlertTriangle;
    text = "We couldn't take your last payment. Update your card to avoid the account becoming read only.";
    cta = 'Update payment';
  } else if (access.warning === 'cancelling' && isAdmin && billing.currentPeriodEnd) {
    text = `Your subscription ends on ${fmtDate(billing.currentPeriodEnd)}. After that the account becomes read only.`;
    cta = 'Keep subscription';
    dismissKey = 'cancelling';
  }

  if (!text || (dismissKey && dismissed === dismissKey)) return null;

  const tones = {
    warn: { background: 'var(--warn-bg-soft)', borderColor: 'var(--warn-border)', color: 'var(--warn-text)' },
    bad:  { background: 'var(--bad-bg)', borderColor: 'var(--bad-border)', color: 'var(--bad-text)' },
  };
  const Icon = icon;

  return (
    <div
      role={tone === 'bad' ? 'alert' : 'status'}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        padding: '10px 14px', marginBottom: 16, borderRadius: 12, border: '1px solid',
        fontSize: 13, lineHeight: 1.45, ...tones[tone],
      }}
    >
      <Icon size={16} style={{ flexShrink: 0 }} />
      <span style={{ flex: 1, minWidth: 200, color: 'var(--text)' }}>{text}</span>
      {cta && (
        <Link
          to="/billing"
          style={{
            display: 'inline-flex', alignItems: 'center', height: 30, padding: '0 12px', borderRadius: 8,
            background: '#FFC81E', color: '#1C1A14', fontWeight: 600, fontSize: 12.5, textDecoration: 'none',
          }}
        >
          {cta}
        </Link>
      )}
      {dismissKey && (
        <button
          type="button"
          aria-label="Dismiss"
          onClick={() => {
            try { sessionStorage.setItem(KEY, dismissKey); } catch { /* ignore */ }
            setDismissed(dismissKey);
          }}
          style={{ display: 'inline-flex', border: 'none', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', padding: 4 }}
        >
          <X size={15} />
        </button>
      )}
    </div>
  );
}